// Общий модал Trip Builder — владелец agent-compare, импортируется другими модулями.
import { useEffect, useId, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../../i18n';
import styles from './Modal.module.css';

export interface ModalProps {
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /**
   * Без полосы заголовка: содержимое рисует свою шапку само (так сделано окно
   * входа). Заголовок при этом никуда не девается — он остаётся именем диалога
   * для скринридера, а кнопка закрытия переезжает в угол панели.
   */
  bare?: boolean;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

export function Modal({ title, onClose, children, footer, wide, bare }: ModalProps): ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const t = useT();
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Автофокус, возврат фокуса и блокировка скролла body — один раз при монтировании.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Содержимое могло уже забрать фокус себе (autoFocus на поле формы) — тогда
    // не перебиваем: иначе окно входа открывалось с фокусом на крестике, а не
    // на почте, и первый же ввод уходил в никуда.
    if (panel && !panel.contains(document.activeElement)) {
      const focusables = getFocusable(panel);
      (focusables[0] ?? panel).focus();
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc закрывает, Tab/Shift+Tab крутят фокус по кругу внутри панели.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = getFocusable(panel);
      if (focusables.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  function handleOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.target === e.currentTarget) onClose();
  }

  return createPortal(
    <div className={styles.overlay} onMouseDown={handleOverlayMouseDown}>
      <div
        ref={panelRef}
        className={[styles.panel, wide ? styles.wide : '', bare ? styles.bare : '']
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {bare ? (
          <>
            <h2 id={titleId} className="visually-hidden">
              {title}
            </h2>
            <button
              type="button"
              className={`${styles.closeBtn} ${styles.closeFloating}`}
              onClick={onClose}
              aria-label={t('form.close')}
            >
              ✕
            </button>
          </>
        ) : (
          <div className={styles.header}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('form.close')}>
              ✕
            </button>
          </div>
        )}
        <div className={bare ? `${styles.body} ${styles.bodyBare}` : styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
